import type { ScreenerColumnDef } from "../types";

/**
 * The criteria picker's catalogue (screener-ux redesign, 2026-10-05): one
 * list, no Basic/Advanced split. A family is one measure; its variants are
 * the same measure over another period or horizon, or on another basis (a
 * percentile rank, US$ or shares). Different measures are never parent and
 * child. A family with variants opens a sub-screen of them; a family with
 * one is a plain row. Families are presentation only: column ids, raw values
 * and saved screens are unchanged.
 *
 * A hint says what the measure is and how to read it, never the obvious
 * (Ben, 2026-10-19): what it measures, the level that matters, and why
 * companies may be missing (thin coverage, a sign rule). One line on the web,
 * two at most on a phone. Multi-year growth always says "average per year";
 * multi-year price return says "total".
 *
 * Ids are resolved against `meta.columns` at runtime and unknown ids drop
 * (with a warning in development). A test asserts every offered id exists
 * in the shared catalogue as filterable and clears a measured coverage floor
 * (A-13): EV / EBIT (no comparable provider value), the low-target upside
 * (about 2% of the default universe) and the 90-day FY1 EPS revision (re-add
 * after about 2026-12-14, once the 90-day baseline exists) stay out, though
 * they remain valid in the filter DSL so saved screens keep working.
 *
 * Money columns without a USD shadow can only be offered as sign tests
 * (audit-response R7); the ones where that test is meaningless are retired
 * from the picker but stay editable when a saved screen carries them.
 */
export interface PickerVariant {
  /** The column id. */
  readonly id: string;
  /** A short period or basis code (TTM, 3Y, RANK); multi-variant families only. */
  readonly code?: string;
  readonly label?: string;
  readonly hint?: string;
}

export interface PickerFamily {
  /** A stable UI id for the measure, never read as a column id. */
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  /** Extra search terms: abbreviations and other names for the measure. */
  readonly aliases?: readonly string[];
  /** The first variant is the default. */
  readonly variants: readonly PickerVariant[];
}

export interface PickerCategory {
  readonly id: string;
  readonly label: string;
  /** Family ids, in display order. */
  readonly families: readonly string[];
}

// Variant notes say what a period is compared with, in the same words
// everywhere.
const RANK_HINT =
  "Scored 0–100 against every stock we cover, excluding OTC; 100 is highest";
const VS_PRIOR_12_MONTHS = "Compared with the 12 months before";
const VS_PRIOR_YEAR = "Compared with the financial year before";
const VS_SAME_QUARTER =
  "Compared with a year earlier (half-year for twice-yearly reporters)";
const VS_LAST_REPORTED_YEAR = "Compared with the last reported financial year";

interface FamilyOptions {
  hint?: string;
  aliases?: readonly string[];
}

const variant = (
  id: string,
  code: string,
  label: string,
  hint?: string,
): PickerVariant => ({ id, code, label, ...(hint && { hint }) });

const family = (
  id: string,
  label: string,
  variants: PickerVariant[],
  options: FamilyOptions = {},
): PickerFamily => ({ id, label, ...options, variants });

const leaf = (
  id: string,
  label: string,
  column: string,
  options: FamilyOptions = {},
): PickerFamily => ({ id, label, ...options, variants: [{ id: column }] });

/** Each family's one home category, in rail order. */
const HOMES: ReadonlyArray<{
  id: string;
  label: string;
  families: readonly PickerFamily[];
}> = [
  {
    id: "valuation",
    label: "Valuation",
    families: [
      family(
        "pe",
        "P/E",
        [
          variant("pe_ttm", "TTM", "Last 12 months"),
          variant("pe_forward_fy1", "FWD", "Forward, this financial year"),
          variant("pe_forward_fy2", "FWD2", "Forward, next financial year"),
          variant(
            "pe_ttm_rank",
            "RANK",
            "Percentile rank, last 12 months",
            RANK_HINT,
          ),
        ],
        {
          aliases: ["price to earnings", "price earnings", "earnings multiple"],
        },
      ),
      leaf("peg", "PEG ratio", "peg_ratio", {
        aliases: ["price earnings growth"],
      }),
      leaf("ps", "P/S", "ps_ttm", {
        aliases: ["price to sales", "price sales"],
      }),
      leaf("ev_sales", "EV / Sales", "ev_to_sales_ttm", {
        aliases: ["enterprise value to sales"],
      }),
      leaf("pb", "P/B", "pb", { aliases: ["price to book", "book value"] }),
      leaf("p_fcf", "Price / free cash flow", "p_fcf_ttm", {
        aliases: ["p fcf", "price to fcf"],
      }),
      leaf("p_ocf", "Price / operating cash flow", "p_ocf_ttm", {
        aliases: ["price to cash flow"],
      }),
      family(
        "ev_ebitda",
        "EV / EBITDA",
        [
          variant("ev_to_ebitda_ttm", "TTM", "Last 12 months"),
          variant(
            "ev_to_ebitda_ttm_rank",
            "RANK",
            "Percentile rank, last 12 months",
            RANK_HINT,
          ),
        ],
        { aliases: ["enterprise value to ebitda"] },
      ),
      leaf("earnings_yield", "Earnings yield", "earnings_yield"),
      family("fcf_yield", "Free cash flow yield", [
        variant("fcf_yield", "TTM", "Last 12 months"),
        variant(
          "fcf_yield_rank",
          "RANK",
          "Percentile rank, last 12 months",
          RANK_HINT,
        ),
      ]),
    ],
  },
  {
    id: "growth",
    label: "Growth",
    families: [
      family(
        "revenue_growth",
        "Revenue growth",
        [
          variant(
            "revenue_growth_ttm",
            "TTM",
            "Last 12 months",
            VS_PRIOR_12_MONTHS,
          ),
          variant(
            "revenue_growth_fy",
            "FY",
            "Last financial year",
            VS_PRIOR_YEAR,
          ),
          variant(
            "revenue_growth_lq",
            "QTR",
            "Latest quarter",
            VS_SAME_QUARTER,
          ),
          variant("revenue_cagr_3y", "3Y", "Last 3 years, average per year"),
          variant("revenue_cagr_5y", "5Y", "Last 5 years, average per year"),
          variant(
            "revenue_growth_ttm_rank",
            "RANK",
            "Percentile rank, last 12 months",
            RANK_HINT,
          ),
        ],
        { aliases: ["sales growth", "top line growth"] },
      ),
      family(
        "eps_growth",
        "EPS growth",
        [
          variant(
            "eps_growth_ttm",
            "TTM",
            "Last 12 months",
            VS_PRIOR_12_MONTHS,
          ),
          variant("eps_growth_fy", "FY", "Last financial year", VS_PRIOR_YEAR),
          variant("eps_cagr_3y", "3Y", "Last 3 years, average per year"),
          variant("eps_cagr_5y", "5Y", "Last 5 years, average per year"),
        ],
        { aliases: ["earnings growth", "earnings per share"] },
      ),
      leaf(
        "net_income_growth",
        "Net income growth (latest year)",
        "net_income_growth_fy",
        { aliases: ["profit growth", "earnings growth"] },
      ),
      leaf("ebitda_growth", "EBITDA growth (latest year)", "ebitda_growth_fy"),
      leaf(
        "ocf_growth",
        "Operating cash flow growth (latest year)",
        "ocf_growth_fy",
      ),
      family("fcf_growth", "Free cash flow growth", [
        variant("fcf_growth_fy", "FY", "Last financial year", VS_PRIOR_YEAR),
        variant("fcf_cagr_3y", "3Y", "Last 3 years, average per year"),
      ]),
      leaf("rule_of_40", "Rule of 40", "rule_of_40", {
        hint: "Revenue growth plus FCF margin; 40+ is the usual bar, needs $100M+ revenue",
      }),
    ],
  },
  {
    id: "profitability",
    label: "Profitability",
    families: [
      family("gross_margin", "Gross margin", [
        variant("gross_margin_ttm", "TTM", "Last 12 months"),
        variant(
          "gross_margin_ttm_rank",
          "RANK",
          "Percentile rank, last 12 months",
          RANK_HINT,
        ),
      ]),
      leaf("operating_margin", "Operating margin", "operating_margin_ttm"),
      leaf("ebitda_margin", "EBITDA margin", "ebitda_margin_ttm"),
      leaf("net_margin", "Net profit margin", "net_margin_ttm", {
        aliases: ["profit margin"],
      }),
      leaf("fcf_margin", "Free cash flow margin", "fcf_margin_ttm"),
      family(
        "roic",
        "Return on invested capital",
        [
          variant("roic_ttm", "TTM", "Last 12 months"),
          variant(
            "roic_ttm_rank",
            "RANK",
            "Percentile rank, last 12 months",
            RANK_HINT,
          ),
        ],
        { aliases: ["roic"] },
      ),
      leaf("roe", "Return on equity", "roe_ttm", { aliases: ["roe"] }),
      leaf("roa", "Return on assets", "roa_ttm", { aliases: ["roa"] }),
      leaf("gp_assets", "Gross profit / assets", "gross_profit_to_assets"),
    ],
  },
  {
    id: "quality",
    label: "Quality & efficiency",
    families: [
      leaf("piotroski", "Piotroski F-score", "piotroski_f", {
        hint: "Nine pass/fail health checks; 8–9 is strong, 0–2 is weak",
        aliases: ["f score"],
      }),
      leaf("income_quality", "Income quality", "income_quality", {
        hint: "Operating cash flow ÷ net income; above 1 means cash backs the profits",
      }),
      leaf("asset_turnover", "Asset turnover", "asset_turnover"),
      leaf("ccc", "Cash conversion cycle", "cash_conversion_cycle", {
        aliases: ["ccc", "working capital"],
      }),
      leaf("dso", "Days sales outstanding", "dso", {
        aliases: ["dso", "receivable days"],
      }),
      leaf("dio", "Days inventory outstanding", "dio", {
        aliases: ["dio", "inventory days"],
      }),
      leaf("dpo", "Days payables outstanding", "dpo", {
        aliases: ["dpo", "payable days"],
      }),
      leaf("capex_revenue", "Capex / revenue", "capex_to_revenue", {
        aliases: ["capital expenditure"],
      }),
      leaf("capex_ocf", "Capex / operating cash flow", "capex_to_ocf", {
        aliases: ["capital expenditure"],
      }),
      leaf("tax_rate", "Effective tax rate", "effective_tax_rate"),
      leaf("rnd", "R&D / revenue", "rnd_to_revenue", {
        aliases: ["research and development"],
      }),
      leaf("sbc", "Stock-based pay / revenue", "sbc_to_revenue", {
        aliases: ["sbc", "stock based compensation", "share based pay"],
      }),
    ],
  },
  {
    id: "dividends",
    label: "Dividends & buybacks",
    families: [
      family(
        "dividend_yield",
        "Dividend yield",
        [
          variant(
            "dividend_yield",
            "REG",
            "Regular dividends, last 12 months",
            "Excludes special dividends",
          ),
          variant(
            "dividend_yield_ltm",
            "ALL",
            "All dividends, last 12 months",
            "Includes special dividends",
          ),
        ],
        { aliases: ["income", "yield"] },
      ),
      family(
        "payout",
        "Payout ratio",
        [
          variant("payout_ratio", "NI", "Of net income"),
          variant("fcf_payout_ratio", "FCF", "Of free cash flow"),
        ],
        { aliases: ["dividend payout", "dividend cover"] },
      ),
      family("dividend_growth", "Dividend growth", [
        variant(
          "dividend_growth_yoy",
          "TTM",
          "Last 12 months",
          VS_PRIOR_12_MONTHS,
        ),
        variant("dividend_cagr_5y", "5Y", "Last 5 years, average per year"),
      ]),
      leaf(
        "dividend_streak",
        "Dividend growth streak",
        "dividend_growth_streak_years",
        { aliases: ["dividend growth years"] },
      ),
      leaf("dividend_frequency", "Dividend frequency", "dividend_frequency"),
      leaf("special_dividend", "Special dividend", "has_special_dividend", {
        hint: "Paid a one-off or special dividend in the last 12 months",
      }),
      leaf("buyback_yield", "Buyback yield", "buyback_yield", {
        hint: "Net cash spent on buybacks over 12 months, as % of market cap",
        aliases: ["share repurchases"],
      }),
      family(
        "dilution",
        "Share dilution",
        [
          variant(
            "shares_change_yoy",
            "FY",
            "Last financial year",
            VS_PRIOR_YEAR,
          ),
          variant("shares_change_lq", "QTR", "Latest quarter", VS_SAME_QUARTER),
          variant("shares_cagr_3y", "3Y", "Last 3 years, average per year"),
        ],
        {
          hint: "Yearly change in share count; above 0 dilutes holders, below 0 is buybacks",
          aliases: ["share count", "buybacks", "shares change"],
        },
      ),
    ],
  },
  {
    id: "balance",
    label: "Balance sheet & debt",
    families: [
      leaf("net_cash", "More cash than debt", "is_net_cash", {
        aliases: ["net cash", "cash rich"],
      }),
      leaf("net_debt", "Net debt", "net_debt_usd"),
      family(
        "nd_ebitda",
        "Net debt / EBITDA",
        [
          variant("net_debt_to_ebitda", "TTM", "Last 12 months"),
          variant(
            "net_debt_to_ebitda_rank",
            "RANK",
            "Percentile rank, last 12 months",
            RANK_HINT,
          ),
        ],
        {
          hint: "Years of EBITDA needed to repay net debt; below 0 means net cash",
          aliases: ["leverage"],
        },
      ),
      leaf("nd_fcf", "Net debt / free cash flow", "net_debt_to_fcf", {
        hint: "Years of free cash flow to repay net debt; net-cash companies won't match",
      }),
      leaf("debt_equity", "Debt / equity", "debt_to_equity", {
        aliases: ["leverage", "gearing"],
      }),
      leaf("debt_assets", "Debt / assets", "debt_to_assets"),
      leaf("current_ratio", "Current ratio", "current_ratio"),
      leaf("quick_ratio", "Quick ratio", "quick_ratio"),
      leaf("interest_coverage", "Interest coverage", "interest_coverage", {
        aliases: ["times interest earned"],
      }),
      leaf("altman_z", "Altman Z-score", "altman_z", {
        hint: "Bankruptcy-risk score: below 1.8 signals distress, above 3 is safe",
        aliases: ["z score", "bankruptcy risk"],
      }),
      leaf(
        "goodwill",
        "Goodwill & intangibles / assets",
        "goodwill_intangibles_to_assets",
        { aliases: ["intangibles"] },
      ),
      leaf("total_equity", "Total equity", "total_equity"),
    ],
  },
  {
    id: "momentum",
    label: "Price & momentum",
    families: [
      family(
        "price_return",
        "Price return",
        [
          variant("ret_1m", "1M", "1 month"),
          variant("ret_3m", "3M", "3 months"),
          variant("ret_6m", "6M", "6 months"),
          variant("ret_ytd", "YTD", "Year to date"),
          variant("ret_1y", "1Y", "1 year"),
          variant("ret_3y", "3Y", "3 years, total"),
          variant("ret_5y", "5Y", "5 years, total"),
          variant("ret_1y_rank", "RANK", "Percentile rank, 1 year", RANK_HINT),
        ],
        {
          hint: "Share price change only, excluding dividends; not annualised",
          aliases: ["performance", "momentum", "share price return"],
        },
      ),
      leaf("from_high", "% below 52-week high", "pct_from_52w_high", {
        hint: "Distance below the 1-year high; 0% means a new high",
        aliases: [
          "52w high",
          "52 week",
          "near 52-week high",
          "from 52-week high",
        ],
      }),
      leaf("from_low", "% above 52-week low", "pct_from_52w_low", {
        hint: "Distance above the 1-year low; 0% means a new low",
        aliases: ["52w low", "52 week", "near 52-week low", "from 52-week low"],
      }),
      family(
        "price_vs_ma",
        "Price vs moving average",
        [
          variant("price_vs_sma_50", "50D", "50-day average"),
          variant("price_vs_sma_200", "200D", "200-day average"),
        ],
        { aliases: ["sma", "moving average", "trend"] },
      ),
      leaf("ma_cross", "50-day vs 200-day average", "sma_50_vs_sma_200", {
        aliases: ["golden cross", "death cross", "sma"],
      }),
      leaf("volatility", "Volatility (1 year)", "volatility_1y", {
        hint: "Annualised swing in daily returns; higher means a bumpier share price",
        aliases: ["risk"],
      }),
      leaf("beta", "Beta", "beta"),
      leaf("relative_volume", "Relative volume", "relative_volume", {
        hint: "Latest day's volume ÷ 30-day average; above 2 is unusually heavy trading",
        aliases: ["rvol"],
      }),
    ],
  },
  {
    id: "analysts",
    label: "Analysts & forecasts",
    families: [
      family(
        "revenue_forecast",
        "Revenue forecast",
        [
          variant(
            "revenue_growth_fy1_est",
            "1Y",
            "This financial year",
            VS_LAST_REPORTED_YEAR,
          ),
          variant(
            "revenue_cagr_2y_est",
            "2Y",
            "Next 2 years, average per year",
          ),
          variant(
            "revenue_cagr_3y_est",
            "3Y",
            "Next 3 years, average per year",
          ),
          variant(
            "revenue_growth_next_2q_est",
            "2Q",
            "Next 2 quarters",
            "Compared with the same 2 quarters a year earlier",
          ),
          variant(
            "revenue_growth_next_4q_est",
            "4Q",
            "Next 4 quarters",
            "Compared with the same 4 quarters a year earlier",
          ),
        ],
        {
          hint: "Wall Street analysts' consensus; companies without coverage won't match",
          aliases: ["revenue estimate", "sales forecast", "consensus"],
        },
      ),
      family(
        "eps_forecast",
        "EPS forecast",
        [
          variant(
            "eps_growth_fy1_est",
            "1Y",
            "This financial year",
            VS_LAST_REPORTED_YEAR,
          ),
          variant(
            "eps_growth_fy2_est",
            "NEXT",
            "Next financial year",
            "Compared with this financial year's forecast",
          ),
          variant("eps_cagr_3y_est", "3Y", "Next 3 years, average per year"),
        ],
        {
          hint: "Wall Street analysts' consensus; companies without coverage won't match",
          aliases: ["earnings forecast", "eps estimate", "consensus"],
        },
      ),
      leaf("consensus_rating", "Consensus rating", "consensus_rating", {
        aliases: ["analyst rating", "recommendation"],
      }),
      family(
        "analyst_ratings",
        "Analyst ratings",
        [
          variant("analyst_buy_count", "BUY", "Buy and strong buy"),
          variant("analyst_hold_count", "HOLD", "Hold"),
          variant("analyst_sell_count", "SELL", "Sell and strong sell"),
        ],
        { aliases: ["buy rating", "sell rating"] },
      ),
      family("analyst_count", "Number of analysts", [
        variant("num_analysts_fy1", "EST", "Earnings estimates, this year"),
        variant("price_target_count", "TGT", "Price targets (US listings)"),
      ]),
      leaf("target_upside", "Price target upside", "price_target_upside", {
        hint: "Average analyst price target vs the last close; US listings only",
        aliases: ["price target", "analyst target"],
      }),
      leaf("target_trend", "Price target trend", "price_target_trend", {
        hint: "Are targets rising? Recent vs 1-year average target; US listings only",
        aliases: ["price target"],
      }),
      leaf("eps_surprise", "EPS surprise (last quarter)", "eps_surprise_last", {
        aliases: ["earnings surprise", "beat", "miss"],
      }),
    ],
  },
  {
    id: "financials",
    label: "Financials",
    families: [
      leaf("revenue", "Revenue", "revenue_ttm_usd", {
        aliases: ["sales", "turnover", "top line"],
      }),
      leaf("operating_income", "Operating income", "operating_income_ttm", {
        aliases: ["ebit", "operating profit"],
      }),
      leaf("ebitda", "EBITDA", "ebitda_ttm"),
      leaf("net_income", "Net income", "net_income_ttm", {
        aliases: ["profit", "earnings"],
      }),
      leaf("ocf", "Operating cash flow", "ocf_ttm", {
        aliases: ["cash from operations"],
      }),
      leaf("fcf", "Free cash flow", "fcf_ttm_usd", {
        aliases: ["fcf", "cash flow"],
      }),
    ],
  },
  {
    id: "size",
    label: "Size & liquidity",
    families: [
      leaf("enterprise_value", "Enterprise value", "enterprise_value_usd", {
        aliases: ["ev"],
      }),
      leaf("shares_outstanding", "Shares outstanding", "shares_outstanding"),
      family(
        "free_float",
        "Free float",
        [
          variant("float_pct", "%", "% of shares outstanding"),
          variant("float_shares", "SHR", "Number of shares"),
        ],
        { aliases: ["float"] },
      ),
      family(
        "avg_volume",
        "Average daily volume (30 days)",
        [
          variant("avg_dollar_volume_30d_usd", "USD", "In US dollars"),
          variant("avg_volume_30d", "SHR", "In shares"),
        ],
        { aliases: ["liquidity", "volume", "dollar volume"] },
      ),
      leaf("employees", "Employees", "employees", {
        aliases: ["headcount", "staff"],
      }),
    ],
  },
];

export const FAMILIES: readonly PickerFamily[] = HOMES.flatMap(
  (home) => home.families,
);

export const POPULAR_CATEGORY_ID = "popular";

/** The default category and the beginner's list, in display order. */
export const POPULAR: readonly string[] = [
  "pe",
  "revenue_growth",
  "revenue_forecast",
  "eps_growth",
  "fcf",
  "net_margin",
  "roic",
  "dividend_yield",
  "nd_ebitda",
  "net_cash",
  "dilution",
  "price_return",
];

export const CATEGORIES: readonly PickerCategory[] = [
  { id: POPULAR_CATEGORY_ID, label: "Popular", families: POPULAR },
  ...HOMES.map((home) => ({
    id: home.id,
    label: home.label,
    families: home.families.map((entry) => entry.id),
  })),
];

/** Every column id the picker can add. */
export const OFFERED_COLUMN_IDS: ReadonlySet<string> = new Set(
  FAMILIES.flatMap((entry) => entry.variants.map((option) => option.id)),
);

/**
 * Out of the picker, still editable when a saved screen carries them: sign
 * tests that are meaningless (revenue, gross profit, assets and cash are
 * never negative; capex nearly always is; "negative debt" means no debt) and
 * the year-2 revenue forecast (hidden 2026-09-25).
 */
export const RETIRED_COLUMN_IDS: ReadonlySet<string> = new Set([
  "revenue_per_employee",
  "revenue_fy",
  "gross_profit_ttm",
  "capex_ttm",
  "total_assets",
  "cash_and_st_investments",
  "total_debt",
  "revenue_estimate_fy1",
  "revenue_growth_fy2_est",
]);

/** The columns a criteria row can edit: everything offered, plus the retired. */
export const EDITABLE_COLUMN_IDS: ReadonlySet<string> = new Set([
  ...Array.from(OFFERED_COLUMN_IDS),
  ...Array.from(RETIRED_COLUMN_IDS),
]);

const FAMILY_OF_COLUMN = new Map(
  FAMILIES.flatMap((entry) =>
    entry.variants.map((option) => [option.id, entry] as const),
  ),
);

export const familyOfColumn = (columnId: string): PickerFamily | undefined =>
  FAMILY_OF_COLUMN.get(columnId);

/** A money column without a USD shadow: offered as a sign test only. */
export const isSignOnly = (column: ScreenerColumnDef): boolean =>
  column.kind === "number" &&
  (column.unit === "money" || column.unit === "money_trading");

export interface ResolvedVariant {
  column: ScreenerColumnDef;
  code?: string;
  label?: string;
  hint?: string;
}

export interface ResolvedFamily {
  id: string;
  label: string;
  hint?: string;
  aliases: readonly string[];
  /** Served variants; a family left with one is a plain row. */
  variants: ResolvedVariant[];
}

export interface ResolvedCategory {
  id: string;
  label: string;
  families: ResolvedFamily[];
}

/**
 * The catalogue against the served columns: unknown or non-filterable ids
 * drop out (with a warning in development so a typo never hides silently),
 * and a family left with no variants drops.
 */
export const resolveCatalogue = (
  columns: Map<string, ScreenerColumnDef>,
): ResolvedCategory[] => {
  const lookup = (id: string): ScreenerColumnDef | undefined => {
    const column = columns.get(id);
    if (column?.filterable) return column;
    if (process.env.NODE_ENV === "development")
      console.warn(`pickerCatalogue: unknown or unfilterable column ${id}`);
    return undefined;
  };
  const resolved = new Map(
    FAMILIES.flatMap((entry): Array<[string, ResolvedFamily]> => {
      const variants = entry.variants.flatMap((option) => {
        const column = lookup(option.id);
        if (!column) return [];
        return [
          {
            column,
            code: option.code,
            label: option.label,
            hint: option.hint,
          },
        ];
      });
      if (variants.length === 0) return [];
      return [
        [
          entry.id,
          {
            id: entry.id,
            label: entry.label,
            hint: entry.hint,
            aliases: entry.aliases ?? [],
            variants,
          },
        ],
      ];
    }),
  );
  return CATEGORIES.map((category) => ({
    id: category.id,
    label: category.label,
    families: category.families.flatMap((id) => {
      const entry = resolved.get(id);
      return entry ? [entry] : [];
    }),
  }));
};
